/** @jsx jsx */
import { Box, Button, Heading, Inline, InlineProps, jsx, Stack } from '@front10/base-elements';
import { LeftChevronIcon } from '../../packages/base-elements/Icon/individual-icons';
import { DialogContent, DialogOverlay } from '@reach/dialog';
import { PropsWithChildren } from 'react';
import { useFullViewport } from '@front10/utils';
import { CSSProperties } from '@front10';

interface MobileFilterDialogProps {
  isVisible?: boolean;
  label: string;
  onDismiss?: () => void;
}

export function MobileFilterDialog(props: PropsWithChildren<MobileFilterDialogProps>) {
  const isVisible = props.isVisible ?? true;
  const label = props.label ?? 'Filters';

  useFullViewport(isVisible);

  return (
    <DialogOverlay
      sx={{
        display: isVisible ? undefined : 'none',
        position: 'fixed',
        top: 0,
        left: 0,
        variant: 'layout.popup.overlay'
      }}
      onDismiss={props.onDismiss}
      dangerouslyBypassScrollLock={!isVisible}
    >
      <DialogContent
        aria-label={label}
        sx={{
          variant: 'layout.container',
          width: '100vw',
          height: '100vh',
          padding: 0,
          margin: 0,
          overflow: 'hidden',
          bg: 'rgba(0, 0, 0, 80%)',
          display: 'flex',
          justifyContent: 'center'
        }}
        className="dialogContent"
      >
        <MobileFilterView>{props.children}</MobileFilterView>
      </DialogContent>
    </DialogOverlay>
  );
}

export function MobileFilterView(props: PropsWithChildren<{}>) {
  return (
    <Stack
      sx={{
        width: '100%',
        maxWidth: 'sm',
        minHeight: '100vh',
        maxHeight: '100vh',
        overflow: 'hidden',
        bg: 'backgroundMain',
        position: 'relative',
        variant: 'layout.popup.mobileFilterLayout'
      }}
    >
      {props.children}
    </Stack>
  );
}

type HeaderProps = {
  title?: string;
  onBackButtonPress?: () => void;
  headingStyle?: CSSProperties;
};

export function MobileHeader(props: HeaderProps) {
  return (
    <Inline
      alignY="center"
      bg="backgroundSoft"
      pt="2"
      pb="2"
      px="2"
      sx={{ variant: 'layout.popup.mobileFilterHeader' }}
      className="MobileHeader"
    >
      <Button
        variant="icon"
        onClick={props.onBackButtonPress}
        sx={{ paddingLeft: '2', paddingRight: '2' }}
      >
        <LeftChevronIcon
          className="fr-icon fr-rotate"
          size={17}
          sx={{ stroke: 'primary', strokeWidth: '100px' }}
        />
      </Button>
      <Heading
        variant="fontSize3"
        sx={{ textAlign: 'center', flex: 1, color: 'text' }}
        style={props.headingStyle}
      >
        {props.title || ''}
      </Heading>
    </Inline>
  );
}
interface BodyProps {
  sx?: any;
}

export function MobileBody({ children, sx, ...props }: PropsWithChildren<BodyProps>) {
  return (
    <Box
      sx={{
        flex: '1',
        overflow: 'auto',
        variant: 'layout.popup.mobileFilterBody',
        ...sx
      }}
    >
      {children}
    </Box>
  );
}

interface FooterProps {
  growChildren?: boolean;
}

export function MobileFooter({
  growChildren,
  children,
  sx,
  ...props
}: PropsWithChildren<FooterProps & Omit<InlineProps, 'ref'>>) {
  return (
    <Inline
      p="dialogMargin"
      className="MobileFooter"
      sx={{
        ...(growChildren && { '>*': { flex: '1' } }),
        boxShadow: 'footer',
        zIndex: '5',
        width: 'sm',
        maxWidth: '100%',
        bg: 'white',
        position: 'fixed',
        bottom: '0px',
        variant: 'layout.popup.mobileFilterFooter',
        ...sx
      }}
      {...props}
    >
      {children}
    </Inline>
  );
}
